import axios from 'axios'
import z from 'zod'
import { ProviderUserRoleSchema, ProviderUserSchema } from './schema'

// ============================================================================
// Type Definitions
// ============================================================================

type BasePayload = {
  providerUserId: number
  programId: number
  providerId: string
}

type ResendInvitePayload = Omit<BasePayload, 'providerId'>

type EditProviderUserInformationPayload = {
  providerUserId: number
  providerId: string
  first_name: string
  last_name: string
  email: string
  roleId: string
  status?: number
}

type EditProviderUserRolePayload = {
  roleId: number
  providerUserId: number
  providerId: string
}

export type SendProviderUserInvitePayload = {
  email: string
  providerId: string
  roleId: string
  programId: number
  first_name: string
  last_name: string
}

export type DisableProviderPayload = {
  providerUserId: number
  providerId: string
}

// ============================================================================
// API Methods
// ============================================================================

export const providerPermissionManagementApi = {
  /**
   * Fetches all provider users for a given provider
   */
  getProviderUsers: async (providerId: string) => {
    const response = await axios.get(`/admin/providers/${providerId}/users`)
    return z.array(ProviderUserSchema).parse(response.data.data)
  },

  sendInvite: async (payload: SendProviderUserInvitePayload) => {
    const { email, first_name, last_name, providerId, roleId, programId } =
      payload

    const response = await axios.post(`/admin/providers`, {
      email,
      first_name,
      last_name,
      provider_id: providerId,
      role_id: roleId,
      program_id: programId,
    })

    return response.data
  },

  /**
   * Resends an invitation to an existing invited provider user
   */
  resendInvite: async (payload: ResendInvitePayload) => {
    return await axios.post('/admin/providers/resend_invitation', {
      provider_user_id: payload.providerUserId,
      program_id: payload.programId,
    })
  },

  /**
   * Updates information for an existing provider user
   */
  editProviderUserInformation: async (
    payload: EditProviderUserInformationPayload
  ) => {
    const {
      email,
      first_name,
      last_name,
      providerId,
      providerUserId,
      roleId,
      status,
    } = payload

    const response = await axios.put(
      `/admin/providers/${providerId}/users/${providerUserId}`,
      {
        email,
        first_name,
        last_name,
        role_id: roleId,
        ...(typeof status === 'number' ? { status } : {}),
      }
    )

    return response.data
  },

  /**
   * Updates the role for an existing provider user
   */
  editProviderUserRole: async (
    payload: EditProviderUserRolePayload
  ): Promise<void> => {
    const { roleId, providerId, providerUserId } = payload

    await axios.put(
      `/admin/providers/${providerId}/users/${providerUserId}/roles`,
      { role_id: roleId }
    )
  },

  /**
   * Fetches all available provider roles
   */
  getAllRoles: async () => {
    const response = await axios.get('/admin/providers/roles')

    return z.array(ProviderUserRoleSchema).parse(response.data)
  },

  disableProviderUser: async (payload: DisableProviderPayload) => {
    return await axios.put(
      `/admin/providers/${payload.providerId}/users/${payload.providerUserId}/disable_user`
    )
  },
}
